import { runInAction } from "mobx";
import type { MessageReadBy } from "revolt-api";
import { Client, Message } from "revolt.js";
import { decodeTime } from "ulid";

function _sortMessages(
    client: Client,
    channelId: string,
    loadedIds?: Set<string>,
) {
    let ret: Message[] = [];
    client.messages.forEach((m) => ret.push(m));
    ret = ret.filter((x) => x.channel?._id === channelId);
    if (loadedIds) {
        ret = ret.filter((x) => loadedIds.has(x._id));
    }
    ret.sort((a, b) => (a._id < b._id ? -1 : 1));
    return ret;
}

export function calcMissingReadMarks(
    client: Client,
    channelId: string,
    loadedIds?: Set<string>,
) {
    const messages = _sortMessages(client, channelId, loadedIds);

    // Collect ALL read marks per user across all messages in the channel.
    const readerPositions = new Map<string, MessageReadBy[]>();

    for (const m of messages) {
        for (const mark of m.readMarks) {
            if (!readerPositions.has(mark.user_id)) {
                readerPositions.set(mark.user_id, []);
            }
            readerPositions.get(mark.user_id)!.push(mark);
        }
    }

    // Sort each user's marks by ULID time ascending.
    for (const marks of readerPositions.values()) {
        marks.sort((a, b) => (a._id < b._id ? -1 : 1));
    }

    let updatedCount = 0;

    // Walk backwards and backfill missing reads using nearest-future mark per user.
    for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];

        if (readerPositions.size > 0) {
            const propagated: MessageReadBy[] = [];

            for (const [userId, marks] of readerPositions) {
                if (userId === m.author_id) continue;

                // Skip if this user already has a mark on this message.
                const existing = m.readMarks.find((x) => x.user_id === userId);
                if (existing) continue;

                // Find the last (most recent) mark with ULID > current message's ULID.
                let futureMark: MessageReadBy | undefined;
                for (let j = marks.length - 1; j >= 0; j--) {
                    if (marks[j]._id > m._id) {
                        futureMark = marks[j];
                        break;
                    }
                }
                if (futureMark) {
                    propagated.push({
                        _id: futureMark._id,
                        user_id: futureMark.user_id,
                        message_id: m._id,
                        channel_id: m.channel?._id ?? "",
                    });
                }
            }

            runInAction(() => {
                if (propagated.length > 0) {
                    m.readMarks.push(...propagated);
                    updatedCount++;
                }
            });
        }
    }

    runInAction(() => {
        // Remove self-marks (author marking their own message as read).
        for (const m of messages) {
            const authorId = m.author_id;
            if (!authorId) continue;
            m.readMarks = m.readMarks.filter((x) => x.user_id !== authorId);
        }
    });

    return updatedCount;
}
